import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, ErrorState, PageHead, Pill, Skeleton, Stat, money } from '@fa/ui';
import { api } from '../lib/api.js';
import type { BillingView } from '../lib/types.js';

export function Billing(): ReactNode {
  const query = useQuery({
    queryKey: ['billing'],
    queryFn: () => api<BillingView>('/v1/portal/billing'),
  });

  const d = query.data;
  const mesAtual = new Date().toISOString().slice(0, 7);
  const usoMes = (d?.usage ?? []).filter((u) => u.month === mesAtual).reduce((s, u) => s + u.authorized, 0);

  return (
    <>
      <PageHead
        title="Plano e faturas"
        subtitle={d ? `${d.plan.name} · renovação mensal` : 'Carregando...'}
        actions={
          <a
            className="btn btn-ghost"
            href="mailto:comercial@fatechbrasil.com.br?subject=Mudanca%20de%20plano"
          >
            Falar sobre o plano
          </a>
        }
      />

      {query.isLoading && (
        <div className="card">
          <Skeleton rows={4} />
        </div>
      )}
      {query.isError && (
        <div className="card">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      )}

      {d && (
        <>
          <div className="grid-4">
            <Stat
              label="Notas autorizadas no mês"
              value={
                <>
                  {usoMes.toLocaleString('pt-BR')}
                  {d.plan.monthly_documents && (
                    <small style={{ fontSize: '1rem', color: 'var(--ink-3)' }}>
                      {' '}
                      / {d.plan.monthly_documents.toLocaleString('pt-BR')}
                    </small>
                  )}
                </>
              }
              hint={`excedente: ${money(d.plan.overage_cents / 100)} por nota`}
            />
            <Stat label="Mensalidade" value={money(d.plan.price_cents / 100)} hint={d.plan.name} />
            <Stat
              label="Faturas em aberto"
              value={d.invoices.filter((i) => i.status === 'open' || i.status === 'overdue').length}
              hint="cobrança mensal"
            />
            <Stat label="Guarda legal" value="5 anos" hint="XMLs em armazenamento imutável" />
          </div>

          <Card title="Consumo por mês" padded={false}>
            <table>
              <thead>
                <tr>
                  <th>Mês</th>
                  <th>NF-e</th>
                  <th>NFC-e</th>
                  <th>NFS-e</th>
                  <th className="r">Total</th>
                </tr>
              </thead>
              <tbody>
                {agrupar(d.usage).map((row) => (
                  <tr key={row.month}>
                    <td>{formatMonth(row.month)}</td>
                    <td className="num">{row.nfe.toLocaleString('pt-BR')}</td>
                    <td className="num">{row.nfce.toLocaleString('pt-BR')}</td>
                    <td className="num">{row.nfse.toLocaleString('pt-BR')}</td>
                    <td className="r num">
                      <b>{row.total.toLocaleString('pt-BR')}</b>
                    </td>
                  </tr>
                ))}
                {d.usage.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>
                      Nenhuma emissão registrada ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>

          <Card title="Faturas" padded={false}>
            <table>
              <thead>
                <tr>
                  <th>Período</th>
                  <th className="r">Valor</th>
                  <th>Situação</th>
                  <th>Vencimento</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.period}</td>
                    <td className="r num">{money(invoice.total_cents / 100)}</td>
                    <td>
                      <Pill
                        tone={
                          invoice.status === 'paid'
                            ? 'ok'
                            : invoice.status === 'overdue'
                              ? 'bad'
                              : invoice.status === 'cancelled'
                                ? 'mute'
                                : 'info'
                        }
                      >
                        {INVOICE_LABEL[invoice.status] ?? invoice.status}
                      </Pill>
                    </td>
                    <td className="num">{invoice.due_date ?? '—'}</td>
                    <td className="r" />
                  </tr>
                ))}
                {d.invoices.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>
                      Nenhuma fatura emitida. A primeira fecha ao final do mês.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </>
  );
}

const INVOICE_LABEL: Record<string, string> = {
  open: 'Em aberto',
  issued: 'Emitida',
  paid: 'Paga',
  overdue: 'Vencida',
  cancelled: 'Cancelada',
};

interface UsageRow {
  month: string;
  nfe: number;
  nfce: number;
  nfse: number;
  total: number;
}

function agrupar(usage: { month: string; model: string; authorized: number }[]): UsageRow[] {
  const map = new Map<string, UsageRow>();
  for (const item of usage) {
    const row = map.get(item.month) ?? { month: item.month, nfe: 0, nfce: 0, nfse: 0, total: 0 };
    row[item.model as 'nfe' | 'nfce' | 'nfse'] += item.authorized;
    row.total += item.authorized;
    map.set(item.month, row);
  }
  return [...map.values()].sort((a, b) => (a.month < b.month ? 1 : -1));
}

const formatMonth = (month: string): string => {
  const [year, m] = month.split('-');
  const label = new Date(Number(year), Number(m) - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};
